import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface StockValuationRowDto {
  branchId: string;
  branchNameAr: string;
  branchNameEn: string | null;
  itemId: string;
  itemNameAr: string;
  itemNameEn: string | null;
  itemSku: string;
  onHand: number;
  unitCost: number | null;
  value: number;
}

export interface StockValuationDto {
  rows: StockValuationRowDto[];
  totalValue: number;
  uncostedItems: number;
}

export interface ReorderRuleDto {
  reorderRuleId: string;
  itemId: string;
  itemNameAr: string;
  itemNameEn: string | null;
  itemSku: string;
  branchId: string | null;
  reorderLevel: number;
  reorderQty: number;
  preferredSupplierId: string | null;
  preferredSupplierName: string | null;
}

export interface UpsertReorderRuleRequest {
  itemId: string;
  branchId?: string | null;
  reorderLevel: number;
  reorderQty: number;
  preferredSupplierId?: string | null;
}

export interface ReorderSuggestionDto {
  itemId: string;
  itemNameAr: string;
  itemNameEn: string | null;
  itemSku: string;
  branchId: string | null;
  branchNameAr: string | null;
  branchNameEn: string | null;
  onHand: number;
  reorderLevel: number;
  suggestedQty: number;
  preferredSupplierId: string | null;
  preferredSupplierName: string | null;
}

export function useStockValuation(branchId: string | null) {
  return useQuery({
    queryKey: ['stock-valuation', branchId ?? 'all'],
    queryFn: async () =>
      (await apiClient.get<StockValuationDto>('/api/inventory/valuation', { params: branchId ? { branchId } : {} })).data,
  });
}

export function useReorderRules() {
  return useQuery({
    queryKey: ['reorder-rules'],
    queryFn: async () => (await apiClient.get<ReorderRuleDto[]>('/api/inventory/reorder-rules')).data,
  });
}

export function useReorderSuggestions() {
  return useQuery({
    queryKey: ['reorder-suggestions'],
    queryFn: async () => (await apiClient.get<ReorderSuggestionDto[]>('/api/inventory/reorder-suggestions')).data,
  });
}

export function useUpsertReorderRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: UpsertReorderRuleRequest) =>
      (await apiClient.post<ReorderRuleDto>('/api/inventory/reorder-rules', req)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reorder-rules'] });
      qc.invalidateQueries({ queryKey: ['reorder-suggestions'] });
    },
  });
}

export function useDeleteReorderRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/inventory/reorder-rules/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reorder-rules'] });
      qc.invalidateQueries({ queryKey: ['reorder-suggestions'] });
    },
  });
}
